import React, { useCallback, useEffect, useState } from 'react';
import { SITE_SETTINGS_DEFAULTS, coerceSiteSettingBool, settingsToAdminForm } from '../siteSettingsDefaults';
import { useSiteSettings } from '../SiteSettingsContext';
import { AdminSiteSubnav } from './AdminSiteSectionNav';
import AdminSiteContentFields from './AdminSiteContentFields';
import AdminSiteSeoFields from './AdminSiteSeoFields';
import AdminSiteFeaturedPicker from './AdminSiteFeaturedPicker';
import AdminSitePoliciesFields from './AdminSitePoliciesFields';
import AdminSiteAuthFields from './AdminSiteAuthFields';
import AdminSiteShopFields from './AdminSiteShopFields';
import AdminSiteExtraSeoFields from './AdminSiteExtraSeoFields';
import AdminSiteBlogFields from './AdminSiteBlogFields';
import AdminSiteHomeSectionsFields from './AdminSiteHomeSectionsFields';
import AdminSiteAnnouncementFields from './AdminSiteAnnouncementFields';
import AdminSiteContactPageFields from './AdminSiteContactPageFields';
import AdminSiteBrandFields from './AdminSiteBrandFields';
import AdminSiteCheckoutFields from './AdminSiteCheckoutFields';
import AdminSiteMailFields from './AdminSiteMailFields';
import AdminSiteShipFromFields from './AdminSiteShipFromFields';
import { AdminPanelAlert, AdminPanelSection } from './AdminPanelUi';
import { SITE_SETTINGS_SAVE_SCOPE, useSaveButtonLabel } from './useSaveButtonLabel';
function adminHeaders(token) {
  const tok = String(token || '').trim();
  return tok
    ? { 'X-Admin-Session': tok, Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' };
}

function HoursField({ id, label, value, onChange }) {
  return (
    <div className="col-md-4">
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
      <input id={id} className="form-control" value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export default function AdminSitePanel({
  sessionToken,
  setStatus,
  catalogCategories = [],
  catalogProducts = {},
}) {
  const { refresh: refreshSiteSettings } = useSiteSettings();
  const [form, setForm] = useState(() => settingsToAdminForm(SITE_SETTINGS_DEFAULTS));
  const [section, setSection] = useState('contact');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const siteSave = useSaveButtonLabel('Save', { scope: SITE_SETTINGS_SAVE_SCOPE });

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const r = await fetch('/api/admin_settings.php', { headers: adminHeaders(sessionToken) });
      const body = await r.json().catch(() => ({}));
      if (!r.ok || !body.success) {
        setLoadError(body.error || `HTTP ${r.status}`);
        return;
      }
      setForm(settingsToAdminForm(body.settings || SITE_SETTINGS_DEFAULTS));
    } catch (e) {
      setLoadError(e.message || 'Network error');
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    load();
  }, [load]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const persistSettings = useCallback(
    async (overrides = {}, successMessage = 'Site settings saved.') => {
      siteSave.startSaving();
      setStatus('');
      try {
        const ogKeys = [
          'seo_home_og_image',
          'seo_shop_og_image',
          'seo_contact_og_image',
          'seo_about_og_image',
        ];
        const snapshot = { ...form, ...overrides };
        const payload = {
          ...snapshot,
          session_token: sessionToken,
          announcement_enabled: coerceSiteSettingBool(snapshot.announcement_enabled, false) ? 1 : 0,
          announcement_text: String(snapshot.announcement_text ?? '').trim(),
          announcement_link: String(snapshot.announcement_link || '').trim() || null,
        };
        ogKeys.forEach((k) => {
          const v = String(payload[k] || '').trim();
          payload[k] = v || null;
        });
        const r = await fetch('/api/admin_settings.php', {
          method: 'POST',
          headers: adminHeaders(sessionToken),
          body: JSON.stringify(payload),
        });
        const body = await r.json().catch(() => ({}));
        if (!r.ok || !body.success) {
          setStatus(body.error || 'Could not save site settings.');
          siteSave.markFailed();
          return false;
        }
        setForm(settingsToAdminForm(body.settings || snapshot));
        await refreshSiteSettings();
        setStatus(successMessage);
        siteSave.markSaved();
        return true;
      } catch (err) {
        setStatus(err.message || 'Save failed.');
        siteSave.markFailed();
        return false;
      }
    },
    [form, refreshSiteSettings, sessionToken, setStatus, siteSave]
  );

  const save = async (e) => {
    e.preventDefault();
    await persistSettings();
  };

  if (loading) {
    return <div className="alert alert-info">Loading site settings…</div>;
  }

  return (
    <div
      className={`admin-site-panel admin-panel-workspace admin-panel-workspace-root${
        section === 'checkout' ? ' admin-site-panel--checkout' : ''
      }`}
    >
      {loadError ? <AdminPanelAlert variant="danger">{loadError}</AdminPanelAlert> : null}

      <AdminSiteSubnav
        active={section}
        onChange={setSection}
        saving={siteSave.isSaving}
        saveLabel={siteSave.label}
        saveSaved={siteSave.isSaved}
        onReload={load}
        onSaveSection={() => document.getElementById('admin-site-settings-form')?.requestSubmit()}
      />

      <form id="admin-site-settings-form" onSubmit={save} className="admin-site-form">
        {section === 'contact' && (
          <>
        <AdminPanelSection title="Contact & social" subtitle="Phone, email, and social links shown across the site.">
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label" htmlFor="site-phone">
                  Phone (digits)
                </label>
                <input
                  id="site-phone"
                  className="form-control"
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                  placeholder="9680384332"
                  autoComplete="tel"
                />
                <div className="form-text">Header, footer, and contact page.</div>
              </div>
              <div className="col-md-4">
                <label className="form-label" htmlFor="site-whatsapp">
                  WhatsApp (digits, with country code)
                </label>
                <input
                  id="site-whatsapp"
                  className="form-control"
                  value={form.whatsapp}
                  onChange={(e) => setField('whatsapp', e.target.value)}
                  placeholder="919680384332"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label" htmlFor="site-email">
                  Email
                </label>
                <input
                  id="site-email"
                  type="email"
                  className="form-control"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                  autoComplete="email"
                />
              </div>
              <div className="col-12">
                <label className="form-label" htmlFor="site-location">
                  Location line
                </label>
                <input
                  id="site-location"
                  className="form-control"
                  value={form.location_line}
                  onChange={(e) => setField('location_line', e.target.value)}
                  placeholder="Ahmedabad, India"
                />
              </div>
              <InstagramField form={form} setField={setField} />
            </div>
        </AdminPanelSection>

        <FooterFields form={form} setField={setField} />
        <AdminPanelAlert variant="info" className="mb-3">
          Home announcement bar: use the <strong>Home sections</strong> tab.
        </AdminPanelAlert>
        <HoursFields form={form} setField={setField} />
        <AdminSiteContactPageFields form={form} setField={setField} />
        <AdminSiteBrandFields
          form={form}
          setField={setField}
          sessionToken={sessionToken}
          onPersistBrand={(overrides) => persistSettings(overrides, 'Brand settings saved.')}
        />
          </>
        )}

        {section === 'content' && (
          <AdminSiteContentFields
            form={form}
            setField={setField}
            sessionToken={sessionToken}
            onPersistHeroImage={(path) =>
              persistSettings({ seo_home_og_image: path }, 'Hero photo saved — refresh the home page to see it.')
            }
          />
        )}

        {section === 'home-sections' && (
          <>
            <AdminSiteAnnouncementFields form={form} setField={setField} />
            <AdminSiteHomeSectionsFields form={form} setField={setField} />
          </>
        )}

        {section === 'seo' && (
          <AdminSiteSeoFields
            form={form}
            setField={setField}
            sessionToken={sessionToken}
            onPersistOgImage={(key, path) =>
              persistSettings({ [key]: path }, 'Social share image saved.')
            }
          />
        )}

        {section === 'featured' && (
          <AdminSiteFeaturedPicker
            featured={form.featured_products}
            onChange={(list) => setField('featured_products', list)}
          />
        )}

        {section === 'articles' && <AdminSiteBlogFields form={form} setField={setField} />}

        {section === 'policies' && <AdminSitePoliciesFields form={form} setField={setField} />}

        {section === 'auth' && <AdminSiteAuthFields form={form} setField={setField} />}

        {section === 'shop' && (
          <>
          <AdminSiteShopFields
            form={form}
            setField={setField}
            sessionToken={sessionToken}
            catalogCategories={catalogCategories}
            catalogProducts={catalogProducts}
            setStatus={setStatus}
            onPersistCustomJewelryHero={(path) => {
              const store = form.store_copy || {};
              const cj = { ...(store.custom_jewelry || {}), hero_image: path };
              return persistSettings(
                { store_copy: { ...store, custom_jewelry: cj } },
                'Custom jewelry hero photo saved.'
              );
            }}
          />
          <AdminSiteExtraSeoFields form={form} setField={setField} />
          </>
        )}
      </form>

      {section === 'checkout' && (
        <AdminSiteCheckoutFields sessionToken={sessionToken} setStatus={setStatus} />
      )}

      {section === 'shipping-labels' && (
        <AdminSiteShipFromFields sessionToken={sessionToken} setStatus={setStatus} />
      )}

      {section === 'email' && <AdminSiteMailFields sessionToken={sessionToken} setStatus={setStatus} />}

      <div className="admin-panel-scroll-pad" aria-hidden="true" />
    </div>
  );
}

function InstagramField({ form, setField }) {
  return (
    <>
    <div className="col-12">
      <label className="form-label" htmlFor="site-instagram">
        Instagram URL
      </label>
      <input
        id="site-instagram"
        type="url"
        className="form-control"
        value={form.instagram_url}
        onChange={(e) => setField('instagram_url', e.target.value)}
      />
    </div>
    <div className="col-12">
      <label className="form-label" htmlFor="site-instagram-embed">
        Instagram embed (optional)
      </label>
      <textarea
        id="site-instagram-embed"
        className="form-control font-monospace small"
        rows={4}
        value={form.instagram_embed_html || ''}
        onChange={(e) => setField('instagram_embed_html', e.target.value)}
        placeholder="Paste HTML from SnapWidget, Elfsight, or Instagram embed tools. Shown on the home page when set."
      />
      <div className="form-text">
        Leave empty to show product photos from your catalog instead. Only paste code from trusted embed providers.
      </div>
    </div>
    </>
  );
}

function FooterFields({ form, setField }) {
  return (
    <AdminPanelSection title="Footer" subtitle="Tagline and location under your brand in the site footer.">
        <div className="row g-3">
          <div className="col-md-6">
            <label className="form-label" htmlFor="site-footer-tagline">
              Tagline
            </label>
            <input
              id="site-footer-tagline"
              className="form-control"
              value={form.footer_tagline}
              onChange={(e) => setField('footer_tagline', e.target.value)}
            />
          </div>
          <div className="col-md-6">
            <label className="form-label" htmlFor="site-footer-location">
              Location under brand
            </label>
            <input
              id="site-footer-location"
              className="form-control"
              value={form.footer_location}
              onChange={(e) => setField('footer_location', e.target.value)}
            />
          </div>
        </div>
    </AdminPanelSection>
  );
}

function HoursFields({ form, setField }) {
  return (
    <AdminPanelSection title="Business hours" subtitle="Shown on the contact page.">
        <div className="row g-3">
          <HoursField
            id="site-hours-wk"
            label="Monday – Friday"
            value={form.hours_mon_fri}
            onChange={(v) => setField('hours_mon_fri', v)}
          />
          <HoursField
            id="site-hours-sat"
            label="Saturday"
            value={form.hours_sat}
            onChange={(v) => setField('hours_sat', v)}
          />
          <HoursField
            id="site-hours-sun"
            label="Sunday"
            value={form.hours_sun}
            onChange={(v) => setField('hours_sun', v)}
          />
          <div className="col-12">
            <label className="form-label" htmlFor="site-hours-note">
              Note below hours
            </label>
            <textarea
              id="site-hours-note"
              className="form-control"
              rows={2}
              value={form.hours_note}
              onChange={(e) => setField('hours_note', e.target.value)}
            />
          </div>
        </div>
    </AdminPanelSection>
  );
}
